// #753 — News & Topics sub-page. v1.2.0 (#960) reshapes the page to
// be the per-user version of the News Crawler detail view (#959).
// v1.3.0 (#977) adds the engagement-gate status banner.
// v1.4.0 (#1044) — Briefing History is now genuinely played-only:
// the legacy per-call prep path that logged "loaded into context"
// rows at session start was removed and its rows purged (migration
// 0099), so briefing_log holds one row per actually-played
// talk_briefing card.
//
// Sections, top to bottom:
//   0. Search eligibility banner (#977) — is this user due for a news
//      search batch right now, and why / why not. The cron is now
//      engagement-gated, so an empty searches grid for a user who
//      never calls is CORRECT, not broken; this banner explains it.
//   1. Eligible topics for news briefings — top-10 from the
//      endpoint, with an "in rotation" coral chip on top 3.
//   2. News searches — cost header + per-user searches grid,
//      reusing NewsCrawlerSearchesPanel from the global view.
//   3. Briefing History — reads briefing_log: one row per
//      talk_briefing card Berta actually played in a call (#1044).
//
// Pre-#960 the page rendered all of user_interests_structured grouped
// by field with engagement gauges. Post-#960 the page focuses on
// what's ACTUALLY in play for briefings, with the full-interest
// surface remaining on the Memory sub-page.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';
import {
  userSubPageStyles, fmtDate, adminFetch,
} from './userSubPageStyles';
import NewsCrawlerSearchesPanel from './NewsCrawlerSearchesPanel';

const VERSION = 'v1.4.0';

// Top-3 entries by weight are what the cron pre-warms the next time
// this user is DUE (cached_briefings.refresh_for_user max_topics=3).
// The rest of the top-10 are "next in line if weights shift".
const IN_ROTATION_TOP_N = 3;

// Field -> human-readable label for the per-entry row "category"
// pill. Same labels as the Memory page so visual consistency holds.
const FIELD_LABELS = {
  music_artists:    'Music artist',
  hobbies:          'Hobby',
  music_genres:     'Music genre',
  screen_interests: 'Show / film',
  sports_interests: 'Sport',
  places_interests: 'Place',
  other_interests:  'Other',
};

function engagementColor(pct) {
  if (pct == null) return '#aaa';
  if (pct < 30) return '#C0392B';
  if (pct < 70) return '#E8845A';
  return '#27AE60';
}

function EngagementGauge({ pct }) {
  const p = Math.max(0, Math.min(100, pct ?? 50));
  const color = engagementColor(p);
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums',
    }}>
      <div style={{
        width: 70, height: 6, background: '#EDE9E4', borderRadius: 99,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${p}%`, height: '100%', background: color,
          transition: 'width 0.3s ease',
        }} />
      </div>
      <span style={{ color, fontWeight: 600, minWidth: 28, textAlign: 'right' }}>
        {p}
      </span>
    </div>
  );
}

const PAGE_SIZE = 100;

export default function AdminUserNewsTopics() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [eligibleTopics, setEligibleTopics] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [cost, setCost] = useState(null);
  const [searches, setSearches] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [briefings, setBriefings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, nc, b] = await Promise.all([
          adminApi.getUser(id),
          adminFetch(`/api/admin/users/${id}/news-crawler?limit=${PAGE_SIZE}&offset=0`),
          adminFetch(`/api/admin/users/${id}/briefings`),
        ]);
        if (cancelled) return;
        setUser(u);
        setEligibleTopics(nc?.eligible_topics || []);
        setEligibility(nc?.eligibility || null);
        setCost(nc?.cost || null);
        setSearches(nc?.searches || []);
        setHasMore(!!nc?.has_more);
        setOffset(PAGE_SIZE);
        setBriefings(b?.briefings || []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  async function loadMore() {
    try {
      const r = await adminFetch(
        `/api/admin/users/${id}/news-crawler?limit=${PAGE_SIZE}&offset=${offset}`,
      );
      setSearches(prev => prev.concat(r?.searches || []));
      setHasMore(!!r?.has_more);
      setOffset(offset + PAGE_SIZE);
    } catch (e) {
      setError(e.message || 'Failed to load more');
    }
  }

  return (
    <AdminLayout title="News & Topics">
      <style>{userSubPageStyles}</style>
      <style>{`
        .in-rotation-chip {
          display: inline-block; margin-left: 8px;
          padding: 2px 8px; border-radius: 10px;
          background: #E8845A; color: #fff;
          font-size: 0.7rem; font-weight: 600;
          letter-spacing: 0.04em; text-transform: uppercase;
        }
        .field-label {
          color: #888; font-size: 0.72rem;
          text-transform: uppercase; letter-spacing: 0.05em;
          font-weight: 600;
        }
        .elig-banner {
          display: flex; align-items: center; gap: 14px;
          padding: 12px 16px; border-radius: 10px;
          margin: 4px 0 8px; border: 1px solid #EDE9E4;
        }
        .elig-banner.due { background: #EAF6EE; border-color: #BFE3CC; }
        .elig-banner.waiting { background: #FDF6EC; border-color: #F2E2C2; }
        .elig-pill {
          padding: 4px 12px; border-radius: 99px; font-weight: 700;
          font-size: 0.78rem; white-space: nowrap; color: #fff;
        }
        .elig-pill.due { background: #27AE60; }
        .elig-pill.waiting { background: #E8845A; }
        .elig-meta { color: #666; font-size: 0.85rem; line-height: 1.5; }
        .elig-meta b { color: #333; }
      `}</style>

      <Link to={`/admin/users/${id}`} className="back-link">← Back to user</Link>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {user && (
        <div className="page-header">
          <div className="page-title">
            {user.name || '(unnamed)'}<span className="ver">{VERSION}</span>
          </div>
          <div className="page-subtitle">
            {user.email} · News &amp; topics sub-page · per-user News Crawler detail
          </div>
        </div>
      )}

      {/* ── Section 0: Search eligibility (#977) ─────────────────── */}
      {eligibility && (
        <div className={`elig-banner ${eligibility.is_eligible_now ? 'due' : 'waiting'}`}>
          <span className={`elig-pill ${eligibility.is_eligible_now ? 'due' : 'waiting'}`}>
            {eligibility.is_eligible_now ? 'Due now' : 'Not due'}
          </span>
          <div className="elig-meta">
            <div><b>{eligibility.reason}</b></div>
            <div>
              Last search: {fmtDate(eligibility.last_news_search_at) || 'never'}
              {' · '}interval: {eligibility.interval_days}d
              {' · '}pending cards: {eligibility.pending_cards}
              {' · '}called since last search:{' '}
              {eligibility.called_since_last_search ? 'yes' : 'no'}
            </div>
          </div>
        </div>
      )}

      {/* ── Section 1: Eligible topics ───────────────────────────── */}
      <div className="section-title">
        Eligible topics for news briefings
        <span className="section-sub">
          Top 10 by engagement (weight &ge; 0.5). The top {IN_ROTATION_TOP_N} are
          what the cron pre-warms the next time this user is due (see banner above).
        </span>
      </div>
      {eligibleTopics && eligibleTopics.length === 0 && (
        <div className="empty-state">
          No briefing-eligible topics yet (all entries below weight 0.5 or no interests captured).
        </div>
      )}
      {eligibleTopics && eligibleTopics.length > 0 && (
        <div className="data-grid">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '38%' }}>Entry</th>
                <th>Field</th>
                <th>Engagement</th>
                <th>Source</th>
                <th>Last briefing</th>
              </tr>
            </thead>
            <tbody>
              {eligibleTopics.map((t, idx) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600, color: '#E8845A' }}>
                    {t.entry}
                    {idx < IN_ROTATION_TOP_N && (
                      <span className="in-rotation-chip"
                            title="Among the top 3 pre-warmed when this user is next due for a search">
                        in rotation
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="field-label">
                      {FIELD_LABELS[t.field] || t.field}
                    </span>
                  </td>
                  <td><EngagementGauge pct={t.engagement} /></td>
                  <td style={{ color: '#aaa', fontSize: '0.82rem' }}>
                    {t.source}
                  </td>
                  <td style={{ color: '#aaa', fontSize: '0.82rem' }}>
                    {fmtDate(t.last_briefing_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Section 2: News searches (cost + grid) ───────────────── */}
      <div className="section-title">
        News searches
        <span className="section-sub">
          Per-user cost + every search the cron has issued for this user.
        </span>
      </div>
      {!loading && !error && (
        <NewsCrawlerSearchesPanel
          cost={cost}
          searches={searches}
          hasMore={hasMore}
          onLoadMore={loadMore}
          showUserColumn={false}
        />
      )}

      {/* ── Section 3: Briefing History (played-only since #1044) ── */}
      <div className="section-title">
        Briefing History
        <span className="section-sub">
          Last 20 briefings Berta actually played in conversation — one
          row per played talk_briefing card (briefing_log).
        </span>
      </div>
      {briefings && briefings.length > 0 ? (
        <div className="data-grid">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Topic</th>
                <th>Search query</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {briefings.slice(0, 20).map(b => (
                <tr key={b.id}>
                  <td style={{ color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    {fmtDate(b.used_at)}
                  </td>
                  <td style={{ fontWeight: 600, color: '#E8845A' }}>{b.topic}</td>
                  <td style={{ color: '#888', fontSize: '0.82rem' }}>{b.search_query}</td>
                  <td style={{ color: '#666', fontSize: '0.85rem' }}>
                    {b.summary
                      ? b.summary.slice(0, 120) + (b.summary.length > 120 ? '...' : '')
                      : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : briefings ? (
        <div className="empty-state">
          No briefings played yet — a row appears when Berta actually
          plays a talk_briefing card during a call (dealt from the
          eligible topics above once fresh news is cached).
        </div>
      ) : null}
    </AdminLayout>
  );
}
